import type React from "react"
import { useTranslation } from "react-i18next"

import { DateRangeField } from "@/components/fields/date-range-field"
import { inclusiveIntervalEnd, intervalFromInclusiveDates } from "@/utils/date"
import type { DateTime, Interval } from "luxon"

import type { DateRange } from "@mui/x-date-pickers-pro/models"

import {
  INTERVAL_DATE_RANGE_SHORTCUTS,
  toInclusiveShortcutPickerRange,
} from "./interval-data-selection"

interface IntervalDataDateRangeProps {
  dateRange: Interval
  maxDate: DateTime | null
  minDate: DateTime | null
  onDateRangeChange: (nextRange: Interval) => void
}

export const IntervalDataDateRange: React.FC<IntervalDataDateRangeProps> = ({
  dateRange,
  maxDate,
  minDate,
  onDateRangeChange,
}) => {
  const { t } = useTranslation("sites")
  const value: DateRange<DateTime> = [
    dateRange.start ?? null,
    inclusiveIntervalEnd(dateRange),
  ]
  const shortcuts = INTERVAL_DATE_RANGE_SHORTCUTS.map((shortcut) => {
    const range = toInclusiveShortcutPickerRange({
      maxDate,
      minDate,
      startOffset: shortcut.startOffset,
    })
    return {
      disabled: range.disabled,
      getValue: (): DateRange<DateTime> => [range.start, range.end],
      label: t(`intervalData.dateRangeShortcuts.${shortcut.key}`),
    }
  })

  const handleChange = (next: DateRange<DateTime>) => {
    const start = next[0]
    const end = next[1]
    const startIso = start?.toISODate()
    const endIso = end?.toISODate()
    if (
      !start?.isValid ||
      !end?.isValid ||
      !startIso ||
      !endIso ||
      startIso > endIso
    ) {
      return
    }
    onDateRangeChange(intervalFromInclusiveDates(start, end))
  }

  return (
    <DateRangeField
      maxDate={maxDate}
      minDate={minDate}
      onChange={handleChange}
      shortcuts={shortcuts}
      value={value}
    />
  )
}
